import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Button, Message, Icon, Input } from 'semantic-ui-react';

// Firebase (modular v9+)
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, sendPasswordResetEmail, connectAuthEmulator } from 'firebase/auth';
import { db } from '../../utils/firebase';
import { ref, get } from "firebase/database";

export default function LoginDialog({
  intent,
  open,
  onClose,
  onSuccess,
  auth,
  firebaseConfig,
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordForgotFlow, setPasswordForgotFlow] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const authInstance = useMemo(() => {
    if (auth) return auth;
    if (!firebaseConfig) return null;
    if (getApps().length === 0) {
      initializeApp(firebaseConfig);
    }
    const authI = getAuth();
    return authI;
  }, [auth, firebaseConfig]);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setPassword('');
      setError('');
      setInfo('');
      setLoading(false);
      setShowPassword(false);
      setPasswordForgotFlow(false);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!authInstance) {
      setError('Chýba konfigurácia Firebase (auth alebo firebaseConfig).');
      return;
    }
    if (!email || !password) {
      setError('Vyplňte e-mail aj heslo.');
      return;
    }

    setLoading(true);
    setError('');
    setInfo('');
    try {
      const userCred = await signInWithEmailAndPassword(authInstance, email.trim(), password);

      if (intent) {
        const userRef = ref(db, "users/" + userCred.user.uid);
        get(userRef)
          .then((snapshot) => {
            if (snapshot.exists()) {
              const userData = snapshot.val();
              onSuccess?.(userData.alias);
            } else {
              console.log("Žiadne dáta");
            }
          })
          .catch((error) => {
            console.error("Chyba pri čítaní dát:", error);
          });
      } else {
        onSuccess();
      }
      onClose?.();
    } catch (err) {
      let msg = 'Prihlásenie zlyhalo. Skúste znova.';
      const code = err?.code || '';
      switch (code) {
        case 'auth/invalid-email':
          msg = 'Neplatná e-mailová adresa.';
          break;
        case 'auth/user-disabled':
          msg = 'Účet bol deaktivovaný.';
          break;
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          msg = 'Nesprávny e-mail alebo heslo.';
          break;
        case 'auth/too-many-requests':
          msg = 'Príliš veľa pokusov. Skúste to neskôr.';
          break;
        case 'auth/network-request-failed':
          msg = 'Sieťová chyba. Skontrolujte pripojenie.';
          break;
        default:
          msg = err?.message || msg;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!passwordForgotFlow) {
      setPasswordForgotFlow(true);
    }

    if (!authInstance) {
      setError('Chýba konfigurácia Firebase (auth alebo firebaseConfig).');
      return;
    }
    if (!email) {
      setError('Zadajte e-mail, na ktorý vám pošleme reset link.');
      return;
    }

    setLoading(true);
    setError('');
    setInfo('');
    try {
      await sendPasswordResetEmail(authInstance, email.trim());
      setInfo('Na váš e-mail bol odoslaný odkaz na obnovenie hesla.');
    } catch (err) {
      let msg = 'Nepodarilo sa odoslať reset link.';
      if (err.code === 'auth/invalid-email') msg = 'Neplatná e-mailová adresa.';
      if (err.code === 'auth/user-not-found') msg = 'Účet s týmto e-mailom neexistuje.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const canSubmitLogin = email.trim().length > 3 && password.length > 0 && !loading;
  const canSubmitPasswordForgot = email.trim().length > 3 && !loading;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="tiny"
      closeOnDimmerClick={true}
    >
      <Modal.Header>Prihlásenie</Modal.Header>
      <Modal.Content>
        <Form onSubmit={e => {
          if (passwordForgotFlow) { handleForgotPassword(e) }
          else { handleSubmit(e) }
        }} noValidate>
          <Form.Field required>
            <label>E-mail</label>
            <Input
              id="email"
              type="email"
              icon="mail"
              iconPosition="left"
              placeholder="napr. jana@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              disabled={loading}
              required
            />
          </Form.Field>

          {!passwordForgotFlow && <Form.Field required>
            <label>Heslo</label>
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              iconPosition="left"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              disabled={loading}
              required
              action={{
                icon: (
                  <Icon name={showPassword ? 'eye slash' : 'eye'} />
                ),
                type: 'button',
                onClick: () => setShowPassword((v) => !v),
                title: showPassword ? 'Skryť heslo' : 'Zobraziť heslo',
              }}
              icon={<Icon name="lock" />}
            />
          </Form.Field>}

          {error && (
            <Message negative size="small">
              <Message.Content>
                <Icon name="warning circle" /> {error}
              </Message.Content>
            </Message>
          )}
          {info && (
            <Message positive size="small">
              <Message.Content>
                <Icon name="info circle" /> {info}
              </Message.Content>
            </Message>
          )}

          <Button
            type="submit"
            color='yellow'
            fluid
            size="large"
            loading={loading}
            disabled={(!passwordForgotFlow && !canSubmitLogin) || (passwordForgotFlow && !canSubmitPasswordForgot)}
            content={passwordForgotFlow ? "Poslať email na zmenu hesla" : "Prihlásiť"}
          />

          <Button
            type="button"
            basic
            fluid
            size="large"
            disabled={loading}
            onClick={onClose}
            style={{ marginTop: 8 }}
            content="Zrušiť"
          />

          {!passwordForgotFlow && <Button
            type="button"
            basic
            fluid
            size="small"
            disabled={loading}
            onClick={handleForgotPassword}
            style={{ marginTop: 8 }}
            content="Zabudol som heslo"
          />}
        </Form>
      </Modal.Content>

      <style>{`
        @media (max-width: 480px) {
          .ui.modal > .content { padding-top: 1rem; padding-bottom: 1rem; }
          .ui.modal .ui.form .field > label { margin-bottom: .4rem; }
        }
        .ui.dimmer { background-color: rgba(0, 0, 0, 0.5);}
      `}</style>
    </Modal>
  );
}